import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { logout } from '../actions/registerUser';
import useInstallPrompt from '../utils/useInstallPrompt';
import usePushNotifications from '../utils/usePushNotifications';
import ToggleSwitch from './ToggleSwitch';
import './NavMenu.css';

// iOS's own Share glyph (upward arrow out of a box) so the instructions
// point at something that actually matches what's in Safari's toolbar,
// rather than describing it in words alone.
//
// site-wide style.css has an unscoped `svg { position: absolute; width:
// 100%; height: 100% }` reset (built for a decorative "pulse" graphic
// elsewhere) that otherwise blows this up to cover the whole menu panel --
// .nav-menu__share-icon in NavMenu.css exists specifically to out-specify
// it back down to an inline glyph.
const ShareIcon = () => (
  <svg
    className="nav-menu__share-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v12" />
    <path d="M7.5 7.5L12 3l4.5 4.5" />
    <rect x="4.5" y="9.5" width="15" height="11" rx="2.2" />
  </svg>
);

// Points right collapsed, rotates to point down expanded (see
// .nav-menu__chevron--expanded) -- same position:static/explicit-size
// override as ShareIcon above, for the same reason: the site-wide `svg`
// reset would otherwise blow this up too.
const Chevron = ({ expanded }) => (
  <svg
    className={`nav-menu__chevron${expanded ? ' nav-menu__chevron--expanded' : ''}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

// Shared by every top bar (Dashboard, EditProfile) that needs the same
// hamburger -> slide-in panel -> My Card/Edit Profile/Leads/Settings/Log out
// menu, so the open/close/backdrop/Escape behavior exists in one place.
//
// The backdrop and panel are rendered via a portal into document.body,
// not inline where <NavMenu/> sits in each page's JSX. They're
// position:fixed, but fixed elements are still painted within whichever
// ancestor stacking context they're nested in -- and every top bar that
// renders this sits inside something that creates one (Dashboard's
// .tuto1 is `position:relative;z-index:99999999` in style.css;
// EditProfile's is buried in a `.container{overflow:hidden}` several
// wrappers deep). That's what broke the previous inline version: the
// panel/backdrop were stacking-trapped inside those ancestors instead of
// painting at the true root, which is exactly why #profileQrCon (the QR
// popup) is rendered as a bare sibling of .my-profile rather than nested
// anywhere -- same goal, but a portal gets there reliably regardless of
// which page's markup this component ends up inside.
const NavMenu = ({ logout, unreadLeadCount, isAuth, user }) => {
  const [open, setOpen] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const history = useHistory();
  const { canPromptNative, canShowIOSInstructions, promptInstall, dismissAndroid } =
    useInstallPrompt(isAuth);
  const {
    canShowToggle: canShowPushToggle,
    permission: pushPermission,
    subscribed: pushSubscribed,
    busy: pushBusy,
    toggle: togglePush,
  } = usePushNotifications(isAuth);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const close = () => setOpen(false);

  const handleLogout = () => {
    close();
    logout();
    history.push('/login');
  };

  return (
    <>
      <button
        type="button"
        className="nav-menu__hamburger"
        aria-label="Open menu"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span />
        <span />
        <span />
      </button>

      {ReactDOM.createPortal(
        <>
          <div
            className={`nav-menu__backdrop${open ? ' nav-menu__backdrop--open' : ''}`}
            onClick={close}
            aria-hidden={!open}
          />

          <nav
            className={`nav-menu__panel${open ? ' nav-menu__panel--open' : ''}`}
            aria-hidden={!open}
          >
            <div className="nav-menu__panel-header">
              <span className="nav-menu__close" onClick={close} aria-label="Close menu">
                &times;
              </span>
            </div>

            <ul className="nav-menu__list">
              {/* Admins only, and first -- it's a switch out of your own
                  card into the panel, not a peer of the three items
                  below it, and it's what an admin opens this menu
                  looking for.

                  Same client-side signal AdminPanel.js gates the /admin
                  route on: user.isAdmin, derived server-side per request
                  from ADMIN_EMAILS (route/api/auth.js). Like that gate,
                  this is convenience only -- every /api/admin/* call
                  re-derives admin status itself, so hiding the link is
                  not what keeps anyone out, and showing it to a stale
                  session just yields AdminAccessDenied.

                  No spinner-wait like AdminPanel's: `user` is briefly
                  null while GET /api/auth/current is in flight (see
                  AppTopBar.js), and for a menu item "not there yet, then
                  it appears" is correct. AdminPanel needs the wait only
                  because it has to decide a redirect. */}
              {user && user.isAdmin && (
                <li>
                  <Link to="/admin" className="nav-menu__item" onClick={close}>
                    Admin
                  </Link>
                </li>
              )}
              {/* The card itself, first among the ordinary items -- the
                  menu had no way back to it at all, only the back
                  chevron, which is invisible to anyone who arrived by
                  another route. Below Admin because that is a switch out
                  of the card entirely, while this is the card.

                  Routed to userName when there is one, lengthId
                  otherwise -- not lengthId unconditionally. lengthId
                  always exists, but /:id renders Dashboard only when it
                  matches how the user is addressed: Dashboard treats a
                  lengthId as "someone else's URL" once a userName is
                  set, hands off to PublicProfile, and PublicProfile
                  canonicalizes with history.push(`/${userName}`). So the
                  lengthId route costs an extra request, a flash of the
                  visitor view, and -- because it pushes rather than
                  replaces -- a Back button that lands on the lengthId
                  URL and is immediately thrown forward again. userName
                  goes straight to the owner's own view.

                  The fallback is the case where lengthId is already the
                  right answer: with no userName set, Dashboard matches
                  on lengthId directly (see ownsThisUrl). So this covers
                  both without ever taking the redirect. Guarded on
                  `user` for the moment before GET /api/auth/current
                  lands, same as Admin above. */}
              {user && (user.userName || user.lengthId) && (
                <li>
                  <Link
                    to={`/${user.userName || user.lengthId}`}
                    className="nav-menu__item"
                    onClick={close}
                  >
                    My Card
                  </Link>
                </li>
              )}
              <li>
                <Link to="/edit_profile?tab=profile" className="nav-menu__item" onClick={close}>
                  Edit Profile
                </Link>
              </li>
              <li>
                <Link to="/leads" className="nav-menu__item" onClick={close}>
                  Leads
                  {unreadLeadCount > 0 && (
                    <span className="nav-menu__badge">
                      {unreadLeadCount > 99 ? '99+' : unreadLeadCount}
                    </span>
                  )}
                </Link>
              </li>
              <li>
                <Link to="/analytics" className="nav-menu__item" onClick={close}>
                  Analytics
                </Link>
              </li>
              <li>
                <Link to="/edit_profile?tab=settings" className="nav-menu__item" onClick={close}>
                  Settings
                </Link>
              </li>
              {canPromptNative && (
                <li className="nav-menu__install-row">
                  <span
                    className="nav-menu__item nav-menu__install"
                    onClick={promptInstall}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') promptInstall();
                    }}
                  >
                    Add to Home Screen
                  </span>
                  <span
                    className="nav-menu__dismiss"
                    onClick={dismissAndroid}
                    role="button"
                    tabIndex={0}
                    aria-label="Dismiss"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') dismissAndroid();
                    }}
                  >
                    &times;
                  </span>
                </li>
              )}
              {canShowIOSInstructions && (
                <li className="nav-menu__install-row nav-menu__install-row--ios">
                  <div
                    className="nav-menu__install-header"
                    onClick={() => setShowIOSInstructions((v) => !v)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={showIOSInstructions}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') setShowIOSInstructions((v) => !v);
                    }}
                  >
                    <span className="nav-menu__install-label">Add to Home Screen</span>
                    <Chevron expanded={showIOSInstructions} />
                  </div>
                  {showIOSInstructions && (
                    <>
                      <p className="nav-menu__install-note">
                        Required to receive lead notifications on your iPhone.
                      </p>
                      <ol className="nav-menu__install-steps">
                        <li>
                          Tap <ShareIcon /> <strong>Share</strong> in Safari&rsquo;s toolbar
                        </li>
                        <li>
                          Scroll down, tap <strong>Add to Home Screen</strong>
                        </li>
                        <li>
                          Tap <strong>Add</strong> to confirm
                        </li>
                      </ol>
                    </>
                  )}
                </li>
              )}
              {canShowPushToggle && (
                <li>
                  <div className="nav-menu__toggle-row">
                    <ToggleSwitch
                      label="Notifications"
                      checked={pushSubscribed}
                      busy={pushBusy}
                      disabled={pushPermission === 'denied'}
                      onChange={() => togglePush()}
                    />
                  </div>
                  {pushPermission === 'denied' && (
                    <p className="nav-menu__notice nav-menu__notice--tight">
                      Blocked — enable notifications for this site in your browser settings.
                    </p>
                  )}
                </li>
              )}
              <li>
                <span
                  className="nav-menu__item"
                  onClick={handleLogout}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleLogout();
                  }}
                >
                  Log out
                </span>
              </li>
            </ul>
          </nav>
        </>,
        document.body
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  unreadLeadCount: state.registerUser.user ? state.registerUser.user.unreadLeadCount : 0,
  isAuth: state.registerUser.isAuth,
  user: state.registerUser.user,
});

export default connect(mapStateToProps, { logout })(NavMenu);
